import { createContext, createElement, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type { PaidPlanId } from '../../shared/billing/plans'
import { crearCheckout, crearPortalFacturacion, obtenerEstadoFacturacion, type BillingStatus } from '@/lib/billing'
import { useAuth } from '@/hooks/useAuth'

type BillingLifecycle = 'clear' | 'wait' | 'load' | 'refresh'

export interface BillingProviderState {
  userId: string | null
  status: BillingStatus | null
  error: string | null
  loading: boolean
}

interface BillingRequest {
  id: number
  userId: string
}

function emptyBillingProviderState(userId: string | null): BillingProviderState {
  return { userId, status: null, error: null, loading: false }
}

export function createBillingStatusStateMachine() {
  let nextRequestId = 0
  let activeRequestId = 0
  let state = emptyBillingProviderState(null)

  const isCurrent = (request: BillingRequest) => (
    activeRequestId === request.id && state.userId === request.userId
  )

  return {
    snapshot: () => state,
    activate: (userId: string | null) => {
      activeRequestId = ++nextRequestId
      state = emptyBillingProviderState(userId)
      return state
    },
    invalidate: () => { activeRequestId = ++nextRequestId },
    begin: (userId: string): BillingRequest => {
      if (state.userId !== userId) {
        activeRequestId = ++nextRequestId
        state = emptyBillingProviderState(userId)
      }
      const request = { id: ++nextRequestId, userId }
      activeRequestId = request.id
      state = { ...state, error: null, loading: true }
      return request
    },
    succeed: (request: BillingRequest, status: BillingStatus): BillingProviderState | null => {
      if (!isCurrent(request)) return null
      state = { userId: request.userId, status, error: null, loading: false }
      return state
    },
    fail: (request: BillingRequest, error: string): BillingProviderState | null => {
      if (!isCurrent(request)) return null
      state = { userId: request.userId, status: null, error, loading: false }
      return state
    },
  }
}

export function getBillingStatusLifecycle(input: {
  authenticated: boolean
  profileSynchronized: boolean
  checkoutReturned: boolean
}): BillingLifecycle {
  if (!input.authenticated) return 'clear'
  if (!input.profileSynchronized) return 'wait'
  return input.checkoutReturned ? 'refresh' : 'load'
}

interface BillingContextValue {
  status: BillingStatus | null
  error: string | null
  loading: boolean
  refresh: () => Promise<void>
  openCheckout: (plan: PaidPlanId) => Promise<void>
  openPortal: () => Promise<void>
  billingRequired: boolean
}

const BillingContext = createContext<BillingContextValue | null>(null)

function regresoDeCheckout(): boolean {
  return new URLSearchParams(globalThis.location.search).get('checkout') === 'success'
}

function mensajeErrorFacturacionSeguro(error: unknown): string {
  if (error instanceof Error && 'status' in error && error.status === 401) {
    return 'Tu sesión ya no es válida. Vuelve a iniciar sesión para consultar la facturación.'
  }
  return 'No fue posible cargar la facturación. Inténtalo de nuevo.'
}

function useBillingState(): BillingContextValue {
  const { estaAutenticado, perfilSincronizado, usuario } = useAuth()
  const userId = estaAutenticado ? usuario?.id ?? null : null
  const requestMachine = useRef(createBillingStatusStateMachine())
  const [view, setView] = useState<BillingProviderState>(() => requestMachine.current.snapshot())

  const refresh = useCallback(async () => {
    if (!userId || !perfilSincronizado) return
    const request = requestMachine.current.begin(userId)
    setView(requestMachine.current.snapshot())
    try {
      const nextStatus = await obtenerEstadoFacturacion()
      const nextView = requestMachine.current.succeed(request, nextStatus)
      if (nextView) setView(nextView)
    } catch (error) {
      const nextView = requestMachine.current.fail(request, mensajeErrorFacturacionSeguro(error))
      if (nextView) setView(nextView)
      throw error
    }
  }, [perfilSincronizado, userId])

  useEffect(() => {
    const lifecycle = getBillingStatusLifecycle({
      authenticated: Boolean(userId),
      profileSynchronized: perfilSincronizado,
      checkoutReturned: regresoDeCheckout(),
    })

    if (lifecycle === 'clear' || lifecycle === 'wait') {
      setView(requestMachine.current.activate(userId))
      return undefined
    }

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let attempts = 0
    const reconcile = async () => {
      attempts++
      await refresh().catch(() => undefined)
      if (cancelled || lifecycle !== 'refresh') return
      const status = requestMachine.current.snapshot().status
      if (status?.accessMode === 'subscription' || status?.accessMode === 'grace' || status?.accessMode === 'complimentary') return
      if (attempts < 5) timer = setTimeout(() => void reconcile(), 2000)
      else setView(previous => ({ ...previous,
        error: previous.error ?? 'El pago aún se está confirmando. Puedes actualizar el estado en unos momentos.' }))
    }
    void reconcile()
    const reportCompleted = () => { void refresh().catch(() => undefined) }
    globalThis.addEventListener?.('radcore:report-completed', reportCompleted)
    return () => {
      cancelled = true
      clearTimeout(timer)
      globalThis.removeEventListener?.('radcore:report-completed', reportCompleted)
      requestMachine.current.invalidate()
    }
  }, [perfilSincronizado, refresh, userId])

  const state = view.userId === userId ? view : emptyBillingProviderState(userId)

  return {
    status: state.status,
    error: state.error,
    loading: state.loading,
    refresh,
    openCheckout: crearCheckout,
    openPortal: crearPortalFacturacion,
    billingRequired: state.status?.accessMode === 'payment_required' || state.status?.reportsRemaining === 0,
  }
}

export function BillingProvider({ children }: { children: ReactNode }) {
  const billing = useBillingState()
  return createElement(BillingContext.Provider, { value: billing }, children)
}

export function useBilling() {
  const billing = useContext(BillingContext)
  if (!billing) throw new Error('useBilling debe usarse dentro de BillingProvider')
  return billing
}
